// Candle engine: everything the Edge needs to know about one candle, derived
// from its OHLC values alone. No drawing here; this is shared by the renderer,
// the zone grading and the explanation logic, and can be unit-tested on its own.

export function validateOhlc(c) {
  const nums = [c.open, c.high, c.low, c.close];
  if (nums.some((n) => typeof n !== "number" || !Number.isFinite(n))) return "OHLC values must be numbers";
  if (c.high < Math.max(c.open, c.close)) return "High must be at or above both open and close";
  if (c.low > Math.min(c.open, c.close)) return "Low must be at or below both open and close";
  if (c.high < c.low) return "High must be at or above low";
  return null;
}

// green: close above open · red: close below open · doji: open equals close ·
// zero-range: all four equal. Doji and zero-range are not taught in Candle
// Anatomy but must still render and announce correctly.
export function candleType(c) {
  if (c.close > c.open) return "green";
  if (c.close < c.open) return "red";
  return c.high === c.low ? "zeroRange" : "doji";
}

// What a screen reader hears instead of the colour.
export const TYPE_DESCRIPTION = {
  green: "close above open",
  red: "close below open",
  doji: "open equals close",
  zeroRange: "open, high, low, and close are equal",
};

export function candleGeometry(c) {
  const bodyTop = Math.max(c.open, c.close);
  const bodyBottom = Math.min(c.open, c.close);
  return {
    type: candleType(c),
    bodyTop,
    bodyBottom,
    hasUpperWick: c.high > bodyTop,
    hasLowerWick: c.low < bodyBottom,
  };
}

// Chart layout constants in data x-units (one unit = one candle slot).
export const CANDLE_X = {
  bodyHalf: 0.5, // drawn body half-width
  pointHalf: 1.1, // how far either side of the candle a price-point tap still counts
  bodyZoneHalf: 0.65, // tappable body width
  wickZoneHalf: 0.6, // expanded tappable width for thin wicks
};

// Visible price range for one candle: its own range plus headroom.
export function priceDomain(c, pad = 0.14) {
  const range = c.high - c.low || 1;
  return [c.low - range * pad, c.high + range * pad];
}

// How far above/below a price point a tap still counts, as a share of the
// visible price range. At the prototype's chart size this is about 22px each
// way, i.e. a 44px target. A config value, to be tuned against the prototype.
export const DEFAULT_TOLERANCE_SHARE = 0.07;

// Price band for each distinct price level on the candle (low, body bottom,
// body top, high). Neighbouring bands meet at the midpoint between their two
// levels, so bands never overlap: a tap on the body top is never inside the
// High band, however short the upper wick is. Outer edges extend by
// `tolerance`. Levels that coincide (no-wick candles, doji) share one band.
function levelBands(c, tolerance) {
  const g = candleGeometry(c);
  const levels = [...new Set([c.low, g.bodyBottom, g.bodyTop, c.high])].sort((a, b) => a - b);
  const band = new Map();
  levels.forEach((lv, i) => {
    const lo = i > 0 ? Math.max((levels[i - 1] + lv) / 2, lv - tolerance) : lv - tolerance;
    const hi = i < levels.length - 1 ? Math.min((lv + levels[i + 1]) / 2, lv + tolerance) : lv + tolerance;
    band.set(lv, [lo, hi]);
  });
  return (price) => band.get(price);
}

// Correct zones for the six Candle Anatomy elements, in data units.
// Each is an array of rects (see core/zones.js).
export function candleZones(c, { index = 0, tolerance } = {}) {
  const g = candleGeometry(c);
  const [d0, d1] = priceDomain(c);
  const tol = tolerance != null ? tolerance : (d1 - d0) * DEFAULT_TOLERANCE_SHARE;
  const band = levelBands(c, tol);
  const px = [index - CANDLE_X.pointHalf, index + CANDLE_X.pointHalf];
  const point = (price) => [{ x: px, y: band(price) }];

  const bodyY = g.bodyTop > g.bodyBottom ? [g.bodyBottom, g.bodyTop] : [g.bodyTop - tol / 2, g.bodyTop + tol / 2];
  const wx = [index - CANDLE_X.wickZoneHalf, index + CANDLE_X.wickZoneHalf];
  const wick = [];
  if (g.hasUpperWick) wick.push({ x: wx, y: [g.bodyTop, c.high + tol] });
  if (g.hasLowerWick) wick.push({ x: wx, y: [c.low - tol, g.bodyBottom] });

  return {
    open: point(c.open),
    close: point(c.close),
    high: point(c.high),
    low: point(c.low),
    body: [{ x: [index - CANDLE_X.bodyZoneHalf, index + CANDLE_X.bodyZoneHalf], y: bodyY }],
    wick,
    // Exposed for explanation logic: "which part of the candle did this tap hit?"
    band,
    tolerance: tol,
  };
}

// Where each element really is, for drawing "Correct answer" and teaching
// markers. For "wick", the longer visible wick.
export function elementAnchor(c, element, index = 0) {
  const g = candleGeometry(c);
  switch (element) {
    case "open":
    case "close":
    case "high":
    case "low":
      return { x: index, y: c[element] };
    case "body":
      return { x: index, y: (g.bodyTop + g.bodyBottom) / 2 };
    case "wick": {
      const up = c.high - g.bodyTop;
      const down = g.bodyBottom - c.low;
      return up >= down ? { x: index, y: (c.high + g.bodyTop) / 2 } : { x: index, y: (c.low + g.bodyBottom) / 2 };
    }
    default:
      return null;
  }
}

// Semantically named destinations for keyboard and screen-reader placement
// ("highest point, upper body edge, lower body edge, lowest point"), plus the
// body and whichever wicks are visible. Choosing one records a tap there.
export function candleDestinations(c, index = 0) {
  const g = candleGeometry(c);
  const d = [
    { id: "high", label: "Highest point", point: { x: index, y: c.high } },
    { id: "upperBody", label: "Upper body edge", point: { x: index, y: g.bodyTop } },
    { id: "lowerBody", label: "Lower body edge", point: { x: index, y: g.bodyBottom } },
    { id: "low", label: "Lowest point", point: { x: index, y: c.low } },
    { id: "body", label: "Body", point: { x: index, y: (g.bodyTop + g.bodyBottom) / 2 } },
  ];
  if (g.hasUpperWick) d.push({ id: "upperWick", label: "Upper wick", point: { x: index, y: (c.high + g.bodyTop) / 2 } });
  if (g.hasLowerWick) d.push({ id: "lowerWick", label: "Lower wick", point: { x: index, y: (c.low + g.bodyBottom) / 2 } });
  return d;
}

// Axis ticks at a round step, like the reference board's 0.50 steps.
export function priceTicks([lo, hi], maxTicks = 9) {
  const steps = [0.1, 0.25, 0.5, 1, 2, 5, 10, 25, 50, 100];
  const step = steps.find((s) => (hi - lo) / s <= maxTicks) || steps[steps.length - 1];
  const ticks = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) ticks.push(Number(v.toFixed(4)));
  return ticks;
}

// Plain-language name of any point on or around a candle, from the same price
// bands the grading uses, so the words always agree with the grade. Returns a
// short form for marker chips and a long form for sentences:
//   { short: "Top of body", long: "the top of the body" }
export function describeCandleLocation(c, pt, index = 0) {
  const g = candleGeometry(c);
  const z = candleZones(c, { index });
  const inBand = (price) => {
    const b = z.band(price);
    return !!b && pt.y >= b[0] && pt.y <= b[1];
  };
  const at = (short, long) => ({ short, long });
  if (Math.abs(pt.x - index) > CANDLE_X.pointHalf) return at("Off the candle", "outside the candle");
  if (pt.y > c.high + z.tolerance) return at("Above the candle", "above the candle");
  if (pt.y < c.low - z.tolerance) return at("Below the candle", "below the candle");
  if (g.hasUpperWick && inBand(c.high)) return at("Upper wick tip", "the tip of the upper wick");
  if (inBand(g.bodyTop)) return at("Top of body", "the top of the body");
  if (inBand(g.bodyBottom)) return at("Bottom of body", "the bottom of the body");
  if (g.hasLowerWick && inBand(c.low)) return at("Lower wick tip", "the tip of the lower wick");
  if (pt.y > g.bodyBottom && pt.y < g.bodyTop) return at("Middle of body", "the middle of the body");
  if (pt.y > g.bodyTop) return at("Upper wick", "the upper wick");
  return at("Lower wick", "the lower wick");
}

// Wick line is drawn 2.4 units wide in a 40-unit candle slot: half of it, in
// data x-units.
export const WICK_HALF = 1.2 / 40;

// x (data units) of the candle's outer edge at a given price, on the given
// side: the body's side edge where the price lies within the body (edges
// included), otherwise the wick line. Diamonds are drawn with their horizontal
// point exactly here, so they sit beside the candle and touch the spot they
// mark (client: "never on top of it… no gap, no overlap").
export function besideX(c, price, side = "right", index = 0) {
  const g = candleGeometry(c);
  const dir = side === "left" ? -1 : 1;
  const inBody = price >= g.bodyBottom && price <= g.bodyTop;
  return index + dir * (inBody ? CANDLE_X.bodyHalf : WICK_HALF);
}
